import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import { Button } from '../../components/button';
import { Badge } from '../../components/badge';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../../components/table';
import { useClients, useContracts, useData } from '../../contexts/DataContext';
import { ContratoView } from '../../components/ContratoView';
import { FianzaView } from '../../components/FianzaView';
import ContratoFianzasPanel from '../../components/convenio/ContratoFianzasPanel';
import PortalAfianzadorasList from '../../components/portal/PortalAfianzadorasList';
import SaldosClienteCard from '../../components/SaldosClienteCard';
import { usePortalConfig } from '../../hooks/usePortalConfig';
import { useFrozenColumns, frozenCellClass, frozenHeadClass } from '../../hooks/useFrozenColumns';
import { computeDocumentExpiryStatus, getClientExpiryStatus } from '../../utils/documentExpiry';
import {
  computePendingFianzas,
  computeEmittedFianzas,
  dueLabel,
  getFianzaPorcentaje,
  getFianzaStatus,
} from '../../utils/convenioCompliance';
import { ensureFacturas, ESTADO_FIANZA } from '../../utils/fianzaEventos';
import { getFacturasVigentes, getEmisorNombre } from '../../utils/calculations';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import {
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  LinkIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';

// Same shell the client portal convenio page uses, with the fiado's tab set.
const TABS = [
  { key: 'resumen', label: 'Resumen' },
  { key: 'contratos', label: 'Contratos' },
  { key: 'fianzas', label: 'Fianzas' },
  { key: 'expediente', label: 'Expediente' },
  { key: 'afianzadoras', label: 'Afianzadoras' },
];
const TAB_KEYS = new Set(TABS.map((t) => t.key));

const REQUIRED_FILES = [
  { id: 'actaConstitutiva', label: 'Acta Constitutiva' },
  { id: 'constanciaSituacionFiscal', label: 'Constancia de Situación Fiscal' },
  { id: 'actasAsambleas', label: 'Actas de Asambleas' },
  { id: 'poderes', label: 'Poderes' },
  { id: 'comprobanteDomicilio', label: 'Comprobante de Domicilio' },
  { id: 'identificacionOficial', label: 'Identificación oficial del apoderado' },
  { id: 'acuseReciboDeclaracion', label: 'Acuse de Recibo de Declaración Anual' },
  { id: 'declaracionEjercicio', label: 'Declaración del Ejercicio' },
  { id: 'estadosFinancierosExcel', label: 'Estados Financieros Declaración Anual (Excel)' },
  { id: 'estadosFinancieros', label: 'Estados Financieros (Contador)' },
  { id: 'estadosFinancierosParciales', label: 'Estados Financieros Parciales (Contador)' },
  { id: 'cedulaContador', label: 'Cédula Profesional del Contador' },
  { id: 'curriculumEmpresarial', label: 'Currículum Empresarial' },
  { id: 'contrato', label: 'Contrato' },
];

const formatMXN = (v) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v || 0);

const formatDate = (ts) => {
  if (!ts) return '-';
  const ms = ts?.toMillis?.() || ts;
  return new Date(ms).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
};

const toMillis = (ts) => ts?.toMillis?.() || ts || 0;

// Case- and accent-insensitive matching for the tab search boxes.
const normalizar = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const FIANZA_ESTATUS_OPTIONS = [
  { value: 'all', label: 'Todos los estatus' },
  { value: 'vigente', label: 'Vigente' },
  { value: 'vencida', label: 'Vencida' },
  { value: 'pendiente', label: 'Por iniciar' },
  { value: 'cancelada', label: 'Cancelada' },
  { value: 'anulada', label: 'Anulada' },
  { value: 'sustituida', label: 'Sustituida' },
];

const FIANZA_PAGO_OPTIONS = [
  { value: 'all', label: 'Pago: Todos' },
  { value: 'pagada', label: 'Pagada' },
  { value: 'parcial', label: 'Parcial' },
  { value: 'pendiente', label: 'Pendiente de pago' },
];

const FIANZA_VIGENCIA_OPTIONS = [
  { value: 'all', label: 'Cualquier vigencia' },
  { value: 'v30', label: 'Vence en ≤ 30 días' },
  { value: 'v90', label: 'Vence en ≤ 90 días' },
  { value: 'vencida', label: 'Vigencia terminada' },
];

const PAGO_BADGE = {
  pagada: 'bg-green-100 text-green-800',
  parcial: 'bg-amber-100 text-amber-800',
  pendiente: 'bg-orange-100 text-orange-800',
};

const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

// The pago rollup for one fianza: same facturas-vigentes rule the broker
// surfaces use, so "Parcial 1/3" means the same thing on both sides.
const pagoInfoFor = (fianza) => {
  const facturasVigentes = getFacturasVigentes(ensureFacturas(fianza));
  const pagadas = facturasVigentes.filter((f) => f.pagada).length;
  const pagoEstado =
    facturasVigentes.length > 0 && pagadas === facturasVigentes.length
      ? 'pagada'
      : pagadas > 0
      ? 'parcial'
      : 'pendiente';
  return { pagoEstado, pagadas, facturasTotal: facturasVigentes.length };
};

const ClientPortalCliente = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clientsById, clientsLoading } = useClients();
  const { contractsList, contractsLoading } = useContracts();
  const { linksById } = useData();
  const portalConfig = usePortalConfig();

  const [selectedContrato, setSelectedContrato] = useState(null);
  const [isContratoViewOpen, setIsContratoViewOpen] = useState(false);
  const [selectedFianza, setSelectedFianza] = useState(null);
  const [selectedFianzaContrato, setSelectedFianzaContrato] = useState(null);
  const [isFianzaViewOpen, setIsFianzaViewOpen] = useState(false);
  const [expandedContratoIds, setExpandedContratoIds] = useState(() => new Set());
  const [contratosSearch, setContratosSearch] = useState('');
  const [fianzasSearch, setFianzasSearch] = useState('');
  // Deep links like ?tab=fianzas&estatus=vencida or &vigencia=v30 land with
  // the filter pre-applied (the Inicio alert cards use this).
  const [fianzasEstatus, setFianzasEstatus] = useState(() => {
    const seed = searchParams.get('estatus');
    return FIANZA_ESTATUS_OPTIONS.some((o) => o.value === seed) ? seed : 'all';
  });
  const [fianzasPago, setFianzasPago] = useState('all');
  const [fianzasVigencia, setFianzasVigencia] = useState(() => {
    const seed = searchParams.get('vigencia');
    return FIANZA_VIGENCIA_OPTIONS.some((o) => o.value === seed) ? seed : 'all';
  });

  const cliente = clientsById[clientId];

  const contratos = useMemo(
    () => contractsList.filter((c) => c.clientId === clientId),
    [contractsList, clientId]
  );

  // Tab — unknown / dropped tabs fall back to resumen.
  const rawTab = searchParams.get('tab');
  const activeTab = TAB_KEYS.has(rawTab) ? rawTab : 'resumen';
  const setActiveTab = (tab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };

  const contratosFrozen = useFrozenColumns(2, activeTab);

  // Per-contrato rollup, fiado-safe: lifecycle counts, monto afianzado and the
  // fiado's own pago status. Prima, endosos and comisión are broker data and
  // never reach this page (ContratoFianzasPanel mode='fiado' won't render them,
  // so the rows only carry placeholders there).
  const getContratoFianzaStats = (contrato) => {
    const now = Date.now();
    const fianzas = contrato.fianzas || [];
    const r = {
      emitidas: fianzas.length,
      vigentes: 0,
      porVencer: 0,
      vencidas: 0,
      porIniciar: 0,
      terminadas: 0,
      montoAfianzado: 0,
      pendientes: computePendingFianzas([contrato]),
      rows: [],
    };
    r.reqPendientes = r.pendientes.length;
    fianzas.forEach((fianza) => {
      const status = getFianzaStatus(fianza, now);
      const fin = toMillis(fianza.finVigenciaFianza);
      const pago = pagoInfoFor(fianza);
      if (status.estado === ESTADO_FIANZA.ACTIVA) {
        r.montoAfianzado += Number(fianza.montoAfianzado) || 0;
        if (status.code === 'vigente') {
          r.vigentes += 1;
          if (fin > 0 && fin - now <= THIRTY_DAYS) r.porVencer += 1;
        } else if (status.code === 'vencida') {
          r.vencidas += 1;
        } else {
          r.porIniciar += 1;
        }
      } else {
        r.terminadas += 1;
      }
      r.rows.push({
        fianza: { ...fianza, porcentajeInfo: getFianzaPorcentaje(fianza, contrato) },
        status,
        prima: 0,
        pagoEstado: pago.pagoEstado,
        pagadas: pago.pagadas,
        facturasTotal: pago.facturasTotal,
        endososPendientes: 0,
      });
    });
    return r;
  };

  const allFianzas = useMemo(() => computeEmittedFianzas(contratos), [contratos]);
  const allPendientes = useMemo(() => computePendingFianzas(contratos), [contratos]);

  // ── Expediente data (links + document statuses) ─────────────────────────
  const clientLinks = useMemo(() => {
    if (!cliente?.links) return [];
    return cliente.links.map((id) => linksById[id]).filter(Boolean);
  }, [cliente?.links, linksById]);

  const requestedFileIds = useMemo(() => {
    const ids = new Set();
    for (const link of clientLinks) {
      if (!link?.files) continue;
      for (const [fileId, fileConfig] of Object.entries(link.files)) {
        if (fileConfig?.required) ids.add(fileId);
      }
    }
    return ids;
  }, [clientLinks]);

  const requestedFiles = useMemo(() => {
    if (requestedFileIds.size === 0) return REQUIRED_FILES;
    return REQUIRED_FILES.filter((f) => requestedFileIds.has(f.id));
  }, [requestedFileIds]);

  const documentStatuses = useMemo(() => {
    const mostRecentByType = {};
    for (const link of clientLinks) {
      if (!link?.filesData) continue;
      for (const [fileDocId, fileDoc] of Object.entries(link.filesData)) {
        if (!fileDoc?.results || fileDoc.status !== 'procesado') continue;
        const underscoreIdx = fileDocId.indexOf('_');
        if (underscoreIdx === -1) continue;
        const fileType = fileDocId.substring(0, underscoreIdx);
        const timestamp = fileDocId.substring(underscoreIdx + 1);
        const existing = mostRecentByType[fileType];
        if (!existing || timestamp > existing.timestamp) {
          mostRecentByType[fileType] = {
            timestamp,
            results: fileDoc.results,
            fileName: fileDoc.fileName || fileDoc.tipoDocumento || fileDocId,
            status: fileDoc.status,
          };
        }
      }
    }
    const statuses = {};
    for (const [fileType, data] of Object.entries(mostRecentByType)) {
      const expiry = computeDocumentExpiryStatus(fileType, data.results);
      statuses[fileType] = { ...data, expiry };
    }
    return statuses;
  }, [clientLinks]);

  const uploadedTypes = requestedFiles.filter((f) => documentStatuses[f.id]).length;
  const completionPct = requestedFiles.length > 0
    ? Math.round((uploadedTypes / requestedFiles.length) * 100)
    : 0;
  const expiredCount = Object.values(documentStatuses).filter((d) => d.expiry?.status === 'expired').length;
  const warningCount = Object.values(documentStatuses).filter((d) => d.expiry?.status === 'warning').length;

  // ── Portfolio rollup for the Resumen tab ────────────────────────────────
  const resumen = useMemo(() => {
    const now = Date.now();
    const day30 = now + 30 * 86400000;
    const day60 = now + 60 * 86400000;
    const day90 = now + 90 * 86400000;

    let vigentes = 0;
    let vencidas = 0;
    let porIniciar = 0;
    let terminadas = 0;
    let montoVigente = 0;
    let montoContratos = 0;
    let pagoPendientes = 0;

    contratos.forEach((c) => {
      montoContratos += Number(c.montoContrato?.value) || 0;
      (c.fianzas || []).forEach((f) => {
        const status = getFianzaStatus(f, now);
        if (status.estado === ESTADO_FIANZA.ACTIVA) {
          if (status.code === 'vigente') {
            vigentes += 1;
            montoVigente += Number(f.montoAfianzado) || 0;
          } else if (status.code === 'vencida') {
            vencidas += 1;
          } else {
            porIniciar += 1;
          }
          if (pagoInfoFor(f).pagoEstado !== 'pagada') pagoPendientes += 1;
        } else {
          terminadas += 1;
        }
      });
    });

    // Emisión pendiente: futuras (vicios ocultos) are NOT actionable and never
    // count next to "por emitir".
    const porEmitir = allPendientes.filter((fr) => !fr.isFutura);
    const atrasadas = porEmitir.filter((fr) => fr.isOverdue);
    const futuras = allPendientes.filter((fr) => fr.isFutura);

    const upcoming = allFianzas
      .map((f) => ({ ...f, _finMs: toMillis(f.finVigenciaFianza) }))
      .filter((f) => {
        if (!(f._finMs > now && f._finMs <= day90)) return false;
        const status = getFianzaStatus(f, now);
        return status.estado === ESTADO_FIANZA.ACTIVA && status.code === 'vigente';
      })
      .sort((a, b) => a._finMs - b._finMs);

    const expBuckets = {
      d30: upcoming.filter((f) => f._finMs <= day30).length,
      d60: upcoming.filter((f) => f._finMs > day30 && f._finMs <= day60).length,
      d90: upcoming.filter((f) => f._finMs > day60 && f._finMs <= day90).length,
    };

    return {
      vigentes, vencidas, porIniciar, terminadas, montoVigente, montoContratos,
      pagoPendientes, porEmitir, atrasadas, futuras, upcoming, expBuckets,
    };
  }, [contratos, allFianzas, allPendientes]);

  const expiryInfo = useMemo(
    () => getClientExpiryStatus(cliente?.links || [], linksById),
    [cliente?.links, linksById]
  );

  const openContrato = (contractId) => {
    const contract = contratos.find((c) => c.id === contractId);
    if (contract) {
      setSelectedContrato(contract);
      setIsContratoViewOpen(true);
    }
  };

  const openFianza = (fianza, contractId) => {
    const contract = contratos.find((c) => c.id === (contractId || fianza.contractId));
    if (!contract) return;
    setSelectedFianzaContrato(contract);
    setSelectedFianza({ ...fianza, contratoId: contract.id });
    setIsFianzaViewOpen(true);
  };

  const toggleContratoExpanded = (contratoId) => {
    setExpandedContratoIds((prev) => {
      const next = new Set(prev);
      if (next.has(contratoId)) next.delete(contratoId);
      else next.add(contratoId);
      return next;
    });
  };

  if (clientsLoading || contractsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azul-surelia" />
      </div>
    );
  }

  if (!cliente) {
    return (
      <div className="text-center py-12">
        <Heading>Cliente no encontrado</Heading>
        <Text className="mt-2 text-gray-500">No existe o no tienes acceso a esta empresa.</Text>
        <Button className="mt-4" onClick={() => navigate('/app/portal')}>
          Volver al Portal
        </Button>
      </div>
    );
  }

  // ── Resumen tab ──────────────────────────────────────────────────────────
  const renderResumen = () => (
    <div className="space-y-4">
      {/* Salud */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <Heading level={4} className="text-azul-surelia mb-4">Salud de tus fianzas</Heading>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-green-50 border border-green-100 rounded-lg p-3">
            <Text className="text-xs text-green-700 uppercase tracking-wide">Monto Afianzado</Text>
            <p className="text-lg font-bold text-green-700">{formatMXN(resumen.montoVigente)}</p>
          </div>
          <button
            onClick={() => setActiveTab('fianzas')}
            className="bg-red-50 border border-red-100 rounded-lg p-3 text-left hover:bg-red-100 transition-colors"
          >
            <Text className="text-xs text-red-700 uppercase tracking-wide">Vencidas</Text>
            <p className="text-lg font-bold text-red-700">{resumen.vencidas}</p>
          </button>
          <button
            onClick={() => setActiveTab('fianzas')}
            className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-left hover:bg-amber-100 transition-colors"
          >
            <Text className="text-xs text-amber-700 uppercase tracking-wide">Por emitir</Text>
            <p className="text-lg font-bold text-amber-700">{resumen.porEmitir.length}</p>
            {resumen.atrasadas.length > 0 && (
              <Text className="text-[11px] text-red-600 font-medium">{resumen.atrasadas.length} atrasada{resumen.atrasadas.length !== 1 ? 's' : ''}</Text>
            )}
          </button>
          <button
            onClick={() => setActiveTab('contratos')}
            className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-left hover:bg-gray-100 transition-colors"
          >
            <Text className="text-xs text-gray-500 uppercase tracking-wide">Contratos</Text>
            <p className="text-lg font-bold text-gray-900">{contratos.length}</p>
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-sm">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-zinc-700">{resumen.vigentes} fianza{resumen.vigentes !== 1 ? 's' : ''} vigente{resumen.vigentes !== 1 ? 's' : ''}</span>
          </span>
          {resumen.porIniciar > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-gray-400" />
              <span className="text-zinc-700">{resumen.porIniciar} por iniciar</span>
            </span>
          )}
          {resumen.terminadas > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-zinc-300" />
              <span className="text-zinc-700">{resumen.terminadas} terminada{resumen.terminadas !== 1 ? 's' : ''}</span>
            </span>
          )}
          {resumen.pagoPendientes > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-orange-500" />
              <span className="text-zinc-700">{resumen.pagoPendientes} con pago pendiente</span>
            </span>
          )}
          {resumen.futuras.length > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-zinc-300" />
              <span className="text-zinc-500">{resumen.futuras.length} futura{resumen.futuras.length !== 1 ? 's' : ''} (vicios ocultos)</span>
            </span>
          )}
        </div>
      </div>

      {/* Expediente */}
      <button
        onClick={() => setActiveTab('expediente')}
        className="w-full bg-white rounded-xl border border-gray-200 shadow-sm p-6 text-left hover:border-azul-surelia/30 hover:shadow-md transition-all"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Heading level={4} className="text-azul-surelia">Expediente</Heading>
            <Text className="text-sm text-gray-500 mt-0.5">
              {uploadedTypes} de {requestedFiles.length} documentos cargados
            </Text>
          </div>
          <div className="flex items-center gap-3">
            {expiredCount > 0 && (
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                {expiredCount} vencido{expiredCount !== 1 ? 's' : ''}
              </span>
            )}
            {warningCount > 0 && (
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                {warningCount} por vencer
              </span>
            )}
            {expiredCount === 0 && warningCount === 0 && (
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                Al día
              </span>
            )}
            <div className="text-2xl font-bold text-azul-surelia tabular-nums">{completionPct}%</div>
          </div>
        </div>
        <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${expiredCount > 0 ? 'bg-red-500' : completionPct === 100 ? 'bg-green-500' : 'bg-azul-surelia'}`}
            style={{ width: `${completionPct}%` }}
          />
        </div>
      </button>

      {/* Próximos vencimientos */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <ClockIcon className="w-5 h-5 text-amber-600" />
          <Heading level={4} className="text-azul-surelia">Próximos Vencimientos</Heading>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-red-50 border border-red-100 rounded-lg p-3 text-center">
            <Text className="text-xs text-red-600">≤ 30 días</Text>
            <p className="text-2xl font-bold text-red-700">{resumen.expBuckets.d30}</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-center">
            <Text className="text-xs text-amber-600">31–60 días</Text>
            <p className="text-2xl font-bold text-amber-700">{resumen.expBuckets.d60}</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-3 text-center">
            <Text className="text-xs text-yellow-700">61–90 días</Text>
            <p className="text-2xl font-bold text-yellow-700">{resumen.expBuckets.d90}</p>
          </div>
        </div>
        {resumen.upcoming.length === 0 ? (
          <Text className="text-sm text-gray-500">No hay fianzas por vencer en los próximos 90 días.</Text>
        ) : (
          <ul className="divide-y divide-gray-100">
            {resumen.upcoming.slice(0, 8).map((f) => {
              const days = Math.max(0, Math.floor((f._finMs - Date.now()) / 86400000));
              const toneClass = days <= 30
                ? 'bg-red-100 text-red-700'
                : days <= 60
                ? 'bg-amber-100 text-amber-700'
                : 'bg-yellow-100 text-yellow-700';
              return (
                <li
                  key={f.id || `${f.contractId}-${f._finMs}`}
                  onClick={() => openFianza(f)}
                  className="py-2 flex items-center gap-3 cursor-pointer hover:bg-zinc-50 -mx-2 px-2 rounded"
                >
                  <span className={`min-w-[64px] text-center px-2 py-1 rounded text-xs font-semibold ${toneClass}`}>
                    {days}d
                  </span>
                  <div className="min-w-0 flex-1">
                    <Text className="text-sm font-medium text-zinc-800 truncate">
                      {f.tipoFianza || f.tipo || 'Fianza'}{f.numeroFianza ? ` — ${f.numeroFianza}` : ''}
                    </Text>
                    <Text className="text-xs text-zinc-500 truncate">
                      {f.contractTitle || f.contractNumero || 'Contrato'}
                    </Text>
                  </div>
                  <span className="text-sm font-semibold text-azul-surelia whitespace-nowrap">
                    {formatMXN(f.montoAfianzado)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Por emitir atrasadas — the fiado's chase list */}
      {resumen.atrasadas.length > 0 && (
        <div className="bg-white rounded-xl border border-red-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
            <Heading level={4} className="text-azul-surelia">Fianzas atrasadas de emisión</Heading>
          </div>
          <ul className="divide-y divide-gray-100">
            {resumen.atrasadas.slice(0, 5).map((fr) => (
              <li
                key={fr._key}
                onClick={() => openContrato(fr.contractId)}
                className="py-2 flex items-center gap-3 cursor-pointer hover:bg-zinc-50 -mx-2 px-2 rounded"
              >
                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800 shrink-0">
                  Atrasada
                </span>
                <div className="min-w-0 flex-1">
                  <Text className="text-sm font-medium text-zinc-800 truncate">{fr.tipo || 'Fianza'}</Text>
                  <Text className="text-xs text-zinc-500 truncate">{fr.contractTitle || fr.contractNumero || 'Contrato'}</Text>
                </div>
                <span className="text-xs text-red-700 font-medium whitespace-nowrap">{dueLabel(fr)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  // ── Contratos tab ────────────────────────────────────────────────────────
  const renderContratos = () => {
    const q = normalizar(contratosSearch.trim());
    const filteredContracts = contratos.filter((c) => {
      if (!q) return true;
      const campos = [
        c.tituloContrato?.value,
        c.numeroContrato?.value,
        c.beneficiario?.value,
        c.contratante?.value,
      ];
      return campos.some((v) => normalizar(v).includes(q));
    });

    return (
      <div className="space-y-4">
        {contratos.length === 0 ? (
          <div className="text-center py-8">
            <Text>No hay contratos registrados.</Text>
          </div>
        ) : (
          <>
            <input
              type="text"
              value={contratosSearch}
              onChange={(e) => setContratosSearch(e.target.value)}
              placeholder="Buscar por título, número o beneficiario..."
              className="w-full sm:max-w-xs px-3 py-2 border border-azul-surelia/30 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-azul-surelia/40 focus:border-azul-surelia"
            />
            <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
              <div className="relative">
                <Table onScroll={contratosFrozen.onScroll}>
                  <TableHead className="bg-azul-surelia">
                    <TableRow>
                      <TableHeader
                        ref={contratosFrozen.setRef(0)}
                        style={{ left: contratosFrozen.offsets[0] }}
                        className={`text-white font-semibold py-3 border-b-0 w-8 ${frozenHeadClass}`}
                      ></TableHeader>
                      <TableHeader
                        ref={contratosFrozen.setRef(1)}
                        style={{ left: contratosFrozen.offsets[1] }}
                        className={`text-white font-semibold py-3 border-b-0 ${frozenHeadClass}`}
                      >No. de Contrato</TableHeader>
                      <TableHeader className="text-white font-semibold py-3 border-b-0">Fianzas</TableHeader>
                      <TableHeader className="text-white font-semibold py-3 border-b-0">Beneficiario</TableHeader>
                      <TableHeader className="text-white font-semibold py-3 border-b-0">Monto Contrato</TableHeader>
                      <TableHeader className="text-white font-semibold py-3 border-b-0">Afianzado</TableHeader>
                      <TableHeader className="text-white font-semibold py-3 border-b-0">Estatus</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredContracts.map((contrato, index) => {
                      const fianzaStats = getContratoFianzaStats(contrato);
                      const expanded = expandedContratoIds.has(contrato.id);
                      return (
                        <React.Fragment key={contrato.id}>
                          <TableRow
                            className={`group cursor-pointer transition-colors duration-200 hover:bg-azul-surelia/5 ${index % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'}`}
                            onClick={() => openContrato(contrato.id)}
                          >
                            <TableCell
                              style={{ left: contratosFrozen.offsets[0] }}
                              className={`py-3 pr-0 ${frozenCellClass(index)}`}
                            >
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleContratoExpanded(contrato.id);
                                }}
                                className="p-1 rounded text-zinc-400 hover:text-azul-surelia hover:bg-azul-surelia/10 transition-all duration-200"
                                title={expanded ? 'Ocultar fianzas' : 'Ver fianzas'}
                              >
                                {expanded ? (
                                  <ChevronDownIcon className="w-4 h-4" />
                                ) : (
                                  <ChevronRightIcon className="w-4 h-4" />
                                )}
                              </button>
                            </TableCell>
                            <TableCell
                              style={{ left: contratosFrozen.offsets[1] }}
                              className={`py-3 ${frozenCellClass(index)}`}
                            >
                              <Text className="text-sm font-semibold text-zinc-900 truncate max-w-xs" title={contrato.numeroContrato?.value || contrato.tituloContrato?.value || '-'}>
                                {contrato.numeroContrato?.value || contrato.tituloContrato?.value || 'Sin número'}
                              </Text>
                              {contrato.numeroContrato?.value && contrato.tituloContrato?.value && (
                                <Text className="text-xs text-zinc-500 truncate max-w-xs" title={contrato.tituloContrato.value}>
                                  {contrato.tituloContrato.value}
                                </Text>
                              )}
                            </TableCell>
                            <TableCell className="py-3">
                              {fianzaStats.emitidas === 0 && fianzaStats.reqPendientes === 0 ? (
                                <Text className="text-sm text-zinc-400">Sin fianzas</Text>
                              ) : (
                                <div className="flex flex-col gap-1">
                                  <div className="flex flex-wrap items-center gap-1">
                                    {fianzaStats.vigentes > 0 && (
                                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                        {fianzaStats.vigentes} vig.
                                      </span>
                                    )}
                                    {fianzaStats.porVencer > 0 && (
                                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                                        {fianzaStats.porVencer} por vencer
                                      </span>
                                    )}
                                    {fianzaStats.vencidas > 0 && (
                                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                        {fianzaStats.vencidas} venc.
                                      </span>
                                    )}
                                    {fianzaStats.porIniciar > 0 && (
                                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                                        {fianzaStats.porIniciar} por iniciar
                                      </span>
                                    )}
                                    {fianzaStats.terminadas > 0 && (
                                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-zinc-200 text-zinc-700">
                                        {fianzaStats.terminadas} term.
                                      </span>
                                    )}
                                  </div>
                                  {fianzaStats.reqPendientes > 0 && (
                                    <Text className="text-xs font-medium text-orange-600">
                                      {fianzaStats.reqPendientes} por emitir
                                    </Text>
                                  )}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="py-3 text-sm">
                              {contrato.beneficiario?.value || contrato.contratante?.value || '-'}
                            </TableCell>
                            <TableCell className="py-3 text-sm font-semibold text-azul-surelia">
                              {formatMXN(contrato.montoContrato?.value || 0)}
                            </TableCell>
                            <TableCell className="py-3">
                              {fianzaStats.montoAfianzado > 0 ? (
                                <Text className="text-xs text-zinc-700 font-semibold tabular-nums">
                                  {formatMXN(fianzaStats.montoAfianzado)}
                                </Text>
                              ) : (
                                <Text className="text-sm text-zinc-400">—</Text>
                              )}
                            </TableCell>
                            <TableCell className="py-3">
                              <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                                contrato.estatus?.value === 'activo' ? 'bg-green-100 text-green-800' :
                                contrato.estatus?.value === 'completado' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {contrato.estatus?.value || 'N/A'}
                              </span>
                            </TableCell>
                          </TableRow>
                          {expanded && (
                            <TableRow className="bg-azul-surelia/5">
                              {/* Spacer over the frozen columns — the sticky panel
                                  slides UNDER this opaque z-10 cell at the end of
                                  the scroll instead of crossing the freeze divider.
                                  Tint = bg-azul-surelia/5 flattened over white. */}
                              <TableCell colSpan={2} style={{ left: 0 }} className="sticky z-10 bg-[#F4F6F7] p-0" />
                              <TableCell colSpan={5} className="py-3 px-6">
                                <ContratoFianzasPanel
                                  stats={fianzaStats}
                                  contratoId={contrato.id}
                                  onFianzaClick={(cid, f) => openFianza(f, cid)}
                                  canUpload={false}
                                  mode="fiado"
                                  stickyOffset={contratosFrozen.total}
                                />
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
                {/* Freeze divider: outside the scrolling element, pinned to the
                    boundary instead of riding along with the cells. */}
                {contratosFrozen.scrolled && (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute top-0 w-0.5 bg-azul-surelia z-30"
                    style={{ left: contratosFrozen.dividerLeft, bottom: contratosFrozen.dividerBottom }}
                  />
                )}
              </div>
              {filteredContracts.length === 0 && (
                <div className="text-center py-6 text-sm text-gray-500">
                  No hay contratos que coincidan con la búsqueda.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  // ── Fianzas tab ──────────────────────────────────────────────────────────
  const renderFianzas = () => {
    const now = Date.now();
    const q = normalizar(fianzasSearch.trim());

    const filteredFianzas = allFianzas.filter((f) => {
      const status = getFianzaStatus(f, now);
      if (fianzasEstatus !== 'all' && status.code !== fianzasEstatus) return false;
      if (fianzasPago !== 'all' && pagoInfoFor(f).pagoEstado !== fianzasPago) return false;
      if (fianzasVigencia !== 'all') {
        const fin = toMillis(f.finVigenciaFianza);
        if (fianzasVigencia === 'vencida') {
          if (!(fin && fin < now)) return false;
        } else {
          const dias = fianzasVigencia === 'v30' ? 30 : 90;
          if (!(fin > now && fin <= now + dias * 86400000)) return false;
        }
      }
      if (q) {
        const campos = [
          f.numeroFianza, f.tipoFianza || f.tipo, f.contractNumero, f.contractTitle,
          getEmisorNombre(f),
        ];
        if (!campos.some((c) => normalizar(c).includes(q))) return false;
      }
      return true;
    });

    const filteredPendientes = allPendientes.filter((fr) => {
      if (!q) return true;
      const campos = [fr.tipo, fr.contractNumero, fr.contractTitle];
      return campos.some((c) => normalizar(c).includes(q));
    });

    return (
      <div className="space-y-4">
        {/* Saldos con afianzadoras — org-gated (showSaldos portal switch). */}
        {portalConfig.showSaldos && contratos.length > 0 && (
          <SaldosClienteCard contracts={contratos} clientId={clientId} />
        )}

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center flex-wrap">
          <input
            type="text"
            value={fianzasSearch}
            onChange={(e) => setFianzasSearch(e.target.value)}
            placeholder="Buscar por número, tipo, contrato..."
            className="w-full sm:max-w-xs px-3 py-2 border border-azul-surelia/30 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-azul-surelia/40 focus:border-azul-surelia"
          />
          <select
            value={fianzasEstatus}
            onChange={(e) => setFianzasEstatus(e.target.value)}
            className="text-sm border border-azul-surelia/30 rounded-md px-2 py-2 text-azul-surelia"
          >
            {FIANZA_ESTATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            value={fianzasPago}
            onChange={(e) => setFianzasPago(e.target.value)}
            className="text-sm border border-azul-surelia/30 rounded-md px-2 py-2 text-azul-surelia"
          >
            {FIANZA_PAGO_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            value={fianzasVigencia}
            onChange={(e) => setFianzasVigencia(e.target.value)}
            className="text-sm border border-azul-surelia/30 rounded-md px-2 py-2 text-azul-surelia"
          >
            {FIANZA_VIGENCIA_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {filteredFianzas.length !== allFianzas.length && (
            <Text className="text-xs text-zinc-500">
              {filteredFianzas.length} de {allFianzas.length} fianzas
            </Text>
          )}
        </div>

        <Heading level={4} className="text-azul-surelia">Emitidas</Heading>
        {filteredFianzas.length === 0 ? (
          <div className="bg-white rounded-lg border border-azul-surelia/15 py-6 text-center">
            <Text className="text-sm text-zinc-500">
              {allFianzas.length === 0
                ? 'Aún no se ha emitido ninguna fianza.'
                : 'No hay fianzas que coincidan con los filtros.'}
            </Text>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
                  <TableRow>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Tipo</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">No. Fianza</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Contrato</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Afianzadora</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Monto</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Vigencia</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Estatus</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Pago</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFianzas.map((f, index) => {
                    const status = getFianzaStatus(f, now);
                    const pago = pagoInfoFor(f);
                    return (
                      <TableRow
                        key={f.id || `${f.contractId}-${index}`}
                        className={`cursor-pointer transition-colors hover:bg-azul-surelia/5 ${index % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'}`}
                        onClick={() => openFianza(f)}
                      >
                        <TableCell className="py-3 text-sm">{f.tipoFianza || f.tipo || '-'}</TableCell>
                        <TableCell className="py-3 text-sm font-mono">{f.numeroFianza || '-'}</TableCell>
                        <TableCell className="py-3 text-sm">
                          <span className="truncate max-w-[220px] inline-block align-bottom" title={f.contractTitle || ''}>
                            {f.contractTitle || f.contractNumero || '-'}
                          </span>
                        </TableCell>
                        <TableCell className="py-3 text-sm">{getEmisorNombre(f) || '-'}</TableCell>
                        <TableCell className="py-3 text-sm font-semibold text-azul-surelia">
                          {formatMXN(f.montoAfianzado || 0)}
                        </TableCell>
                        <TableCell className="py-3 text-xs text-gray-600">
                          {formatDate(f.inicioVigenciaFianza)} — {formatDate(f.finVigenciaFianza)}
                        </TableCell>
                        <TableCell className="py-3">
                          <Badge color={status.color}>{status.label}</Badge>
                        </TableCell>
                        <TableCell className="py-3">
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${PAGO_BADGE[pago.pagoEstado]}`}>
                            {pago.pagoEstado === 'pagada' ? 'Pagada' : pago.pagoEstado === 'parcial' ? `Parcial ${pago.pagadas}/${pago.facturasTotal}` : 'Pendiente'}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* ── Por emitir y futuras ─────────────────────────────────────── */}
        <div className="pt-2">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
            <Heading level={4} className="text-azul-surelia">Por emitir y futuras</Heading>
            <Text className="text-xs text-zinc-500">
              {filteredPendientes.length} requisito{filteredPendientes.length === 1 ? '' : 's'} sin fianza emitida
            </Text>
          </div>
          {filteredPendientes.length === 0 ? (
            <div className="bg-white rounded-lg border border-azul-surelia/15 py-6 text-center">
              <Text className="text-sm text-zinc-500">
                {allPendientes.length === 0
                  ? 'Todas las fianzas requeridas ya fueron emitidas.'
                  : 'Ningún requisito coincide con los filtros.'}
              </Text>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
                    <TableRow>
                      <TableHeader className="text-white font-semibold py-3 border-b-0">Tipo</TableHeader>
                      <TableHeader className="text-white font-semibold py-3 border-b-0">Estado</TableHeader>
                      <TableHeader className="text-white font-semibold py-3 border-b-0">Contrato</TableHeader>
                      <TableHeader className="text-white font-semibold py-3 border-b-0">% del contrato</TableHeader>
                      <TableHeader
                        className="text-white font-semibold py-3 border-b-0 text-right"
                        title="Monto que deberá quedar afianzado cuando se emita la fianza."
                      >Por afianzar</TableHeader>
                      <TableHeader className="text-white font-semibold py-3 border-b-0">Emisión</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPendientes.map((fr, index) => {
                      const tone = fr.isFutura
                        ? { badge: 'bg-zinc-100 text-zinc-600', label: 'Futura', due: 'text-zinc-500' }
                        : fr.isOverdue
                        ? { badge: 'bg-red-100 text-red-800', label: 'Atrasada', due: 'text-red-700 font-medium' }
                        : { badge: 'bg-amber-100 text-amber-800', label: 'Por emitir', due: 'text-amber-700' };
                      return (
                        <TableRow
                          key={fr._key}
                          className={`cursor-pointer hover:bg-azul-surelia/5 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'}`}
                          onClick={() => openContrato(fr.contractId)}
                        >
                          <TableCell className="py-3 text-sm font-medium text-zinc-800">{fr.tipo || 'Fianza'}</TableCell>
                          <TableCell className="py-3">
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${tone.badge}`}>{tone.label}</span>
                          </TableCell>
                          <TableCell className="py-3 text-sm">
                            <span className="truncate max-w-[220px] inline-block align-bottom" title={fr.contractTitle || ''}>
                              {fr.contractTitle || fr.contractNumero || '-'}
                            </span>
                          </TableCell>
                          <TableCell className="py-3 text-sm tabular-nums">
                            {Number(fr.porcentaje) > 0 ? `${Number(fr.porcentaje)}%` : '—'}
                          </TableCell>
                          <TableCell className="py-3 text-sm text-right tabular-nums font-semibold text-azul-surelia">
                            {formatMXN(fr.monto || 0)}
                          </TableCell>
                          <TableCell className={`py-3 text-xs ${tone.due}`}>{dueLabel(fr)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Expediente tab ───────────────────────────────────────────────────────
  const renderExpediente = () => (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <Text className="text-xs text-gray-400 uppercase tracking-wide">Expediente</Text>
          <div className="mt-2 text-3xl font-bold text-gray-900">{completionPct}%</div>
          <Text className="text-xs text-gray-500 mt-1">{uploadedTypes} de {requestedFiles.length} documentos</Text>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <Text className="text-xs text-gray-400 uppercase tracking-wide">Docs Vencidos</Text>
          <div className={`mt-2 text-3xl font-bold ${expiredCount > 0 ? 'text-red-600' : 'text-gray-900'}`}>{expiredCount}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <Text className="text-xs text-gray-400 uppercase tracking-wide">Docs por Vencer</Text>
          <div className={`mt-2 text-3xl font-bold ${warningCount > 0 ? 'text-amber-600' : 'text-gray-900'}`}>{warningCount}</div>
        </div>
      </div>

      {/* Expediente Links */}
      {clientLinks.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-3">
            <LinkIcon className="w-4 h-4 text-azul-surelia" />
            <Heading level={3}>Links de Actualización</Heading>
          </div>
          <Text className="text-xs text-gray-500 mb-3">Ingresa a estos links para actualizar tu documentación.</Text>
          <div className="space-y-2">
            {clientLinks.map((link) => {
              const url = `${window.location.origin}/expediente/${link.id}`;
              const totalRequested = link.files
                ? Object.values(link.files).filter((f) => f.required).length
                : 0;
              const uploadedFiles = link.filesData
                ? Object.values(link.filesData).filter((f) => f.status === 'procesado' || f.status === 'uploaded').length
                : 0;
              return (
                <div key={link.id} className="flex items-center gap-2 py-2 px-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <Text className="text-sm font-medium truncate">{link.name || link.id}</Text>
                    <Text className="text-xs text-azul-surelia font-mono truncate">{url}</Text>
                    {totalRequested > 0 && (
                      <Text className="text-xs text-gray-400 mt-0.5">{uploadedFiles} de {totalRequested} documentos cargados</Text>
                    )}
                  </div>
                  <button
                    onClick={() => { navigator.clipboard.writeText(url); }}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-azul-surelia hover:bg-azul-surelia/10 transition-colors flex-shrink-0"
                    title="Copiar link"
                  >
                    <ClipboardDocumentIcon className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Vigencia Alerts */}
      {(expiredCount > 0 || warningCount > 0) && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <Heading level={3} className="mb-1">Alertas de Vigencia</Heading>
          <Text className="text-xs text-gray-500 mb-3">Contacta a tu agente para actualizar tus documentos.</Text>
          <div className="space-y-2">
            {Object.entries(documentStatuses)
              .filter(([, d]) => d.expiry?.status === 'expired' || d.expiry?.status === 'warning')
              .sort(([, a], [, b]) => {
                if (a.expiry.status === 'expired' && b.expiry.status !== 'expired') return -1;
                if (a.expiry.status !== 'expired' && b.expiry.status === 'expired') return 1;
                return 0;
              })
              .map(([fileType, d]) => {
                const label = REQUIRED_FILES.find((f) => f.id === fileType)?.label || fileType;
                const isExpired = d.expiry.status === 'expired';
                return (
                  <div
                    key={fileType}
                    className={`flex items-center gap-3 p-3 rounded-lg ${
                      isExpired ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'
                    }`}
                  >
                    <ExclamationTriangleIcon
                      className={`w-5 h-5 flex-shrink-0 ${isExpired ? 'text-red-500' : 'text-amber-500'}`}
                    />
                    <div className="min-w-0 flex-1">
                      <Text className={`font-medium text-sm ${isExpired ? 'text-red-800' : 'text-amber-800'}`}>{label}</Text>
                      <Text className={`text-xs ${isExpired ? 'text-red-600' : 'text-amber-600'}`}>{d.expiry.message}</Text>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Document Checklist */}
      <div>
        <Heading level={3} className="mb-3">Documentos</Heading>
        <div className="space-y-2">
          {requestedFiles.map((file) => {
            const docStatus = documentStatuses[file.id];
            const expiry = docStatus?.expiry;
            const hasDoc = !!docStatus;

            let borderColor = 'border-l-gray-300';
            let expiryBadge = null;

            if (!hasDoc) {
              borderColor = 'border-l-gray-300';
              expiryBadge = (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                  No cargado
                </span>
              );
            } else if (!expiry) {
              // No expiry rule for this document type — just show as OK
              borderColor = 'border-l-green-500';
              expiryBadge = null;
            } else if (expiry.status === 'unknown') {
              // Has expiry rule but couldn't extract the date
              borderColor = 'border-l-gray-300';
              expiryBadge = (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                  En revisión
                </span>
              );
            } else if (expiry.status === 'valid') {
              borderColor = 'border-l-green-500';
              expiryBadge = (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                  Vigente
                </span>
              );
            } else if (expiry.status === 'warning') {
              borderColor = 'border-l-amber-400';
              expiryBadge = (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                  Por vencer
                </span>
              );
            } else if (expiry.status === 'expired') {
              borderColor = 'border-l-red-500';
              expiryBadge = (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                  Vencido
                </span>
              );
            }

            return (
              <div
                key={file.id}
                className={`bg-white rounded-lg border border-gray-200 shadow-sm p-4 border-l-4 ${borderColor}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <DocumentTextIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <Text className="font-medium text-sm">{file.label}</Text>
                    </div>
                    {hasDoc && (
                      <div className="mt-1 ml-6">
                        <Text className="text-xs text-gray-500 truncate">{docStatus.fileName}</Text>
                        {expiry?.message && (
                          <Text className={`text-xs mt-0.5 ${
                            expiry.status === 'expired' ? 'text-red-600' : 'text-amber-600'
                          }`}>
                            {expiry.message}
                          </Text>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {hasDoc && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        Cargado
                      </span>
                    )}
                    {expiryBadge}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* All clear message */}
      {expiredCount === 0 && warningCount === 0 && uploadedTypes > 0 && (
        <div className="flex items-center gap-3 text-green-600 bg-green-50 rounded-xl border border-green-200 p-4">
          <CheckCircleIcon className="w-6 h-6" />
          <Text className="text-green-700 font-medium">Todos los documentos están vigentes.</Text>
        </div>
      )}
    </div>
  );

  return (
    <div>
      {/* Header — no back arrow: empresa switching lives in the sidebar
          context selector, like the convenio portal page. */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="flex-1 min-w-0">
          <Heading>{cliente.nombreEmpresa || 'Sin nombre'}</Heading>
          {cliente.rfc && (
            <Text className="text-xs text-gray-500 font-mono mt-1">{cliente.rfc}</Text>
          )}
        </div>
        <Badge color={expiryInfo.worst === 'expired' ? 'red' : expiryInfo.worst === 'warning' ? 'amber' : 'green'}>
          {expiryInfo.worst === 'expired' ? 'Docs vencidos' : expiryInfo.worst === 'warning' ? 'Docs por vencer' : 'Expediente al día'}
        </Badge>
      </div>

      {/* Tabs */}
      <div className="overflow-x-auto mb-6 border-b border-azul-surelia/20">
        <div className="flex gap-1 min-w-max">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 sm:px-6 py-3 font-semibold focus:outline-none transition-all duration-200 border-b-2 whitespace-nowrap text-sm sm:text-base ${
                activeTab === tab.key
                  ? 'text-azul-surelia border-azul-surelia bg-azul-surelia/5'
                  : 'text-gray-500 border-transparent hover:text-azul-surelia hover:border-azul-surelia/40 hover:bg-azul-surelia/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'resumen' && renderResumen()}
      {activeTab === 'contratos' && renderContratos()}
      {activeTab === 'fianzas' && renderFianzas()}
      {activeTab === 'expediente' && renderExpediente()}
      {activeTab === 'afianzadoras' && <PortalAfianzadorasList clients={[cliente]} />}

      {/* ContratoView */}
      {selectedContrato && (
        <ContratoView
          contrato={selectedContrato}
          isOpen={isContratoViewOpen}
          onClose={() => { setIsContratoViewOpen(false); setSelectedContrato(null); }}
          clientData={cliente}
          onUpdate={() => {}}
        />
      )}

      {/* FianzaView */}
      {selectedFianza && selectedFianzaContrato && (
        <FianzaView
          fianza={selectedFianza}
          contratoData={selectedFianzaContrato}
          clientData={cliente}
          isOpen={isFianzaViewOpen}
          onClose={() => {
            setIsFianzaViewOpen(false);
            setSelectedFianza(null);
            setSelectedFianzaContrato(null);
          }}
          onUpdate={() => {}}
        />
      )}
    </div>
  );
};

export default ClientPortalCliente;
